import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  Tag,
  TagLabel,
  HStack,
  Skeleton,
  Select,
  Button,
} from "@chakra-ui/react";
import { fetchAMUpdates } from "../../amUpdates/services";
import { format } from "date-fns";
import { UPDATE_CATEGORIES } from "views/admin/amUpdates";

interface Update {
  id: number;
  accountManagerId: number;
  partnerId: number;
  date: string;
  updateCategories: string[];
  updateNote: string;
}

interface AMUpdatesViewProps {
  partnerId: number | null;
  accountManagers: any[];
}

interface PaginationState {
  page: number;
  limit: number;
  total: number;
}

const AMUpdatesView = ({ partnerId, accountManagers }: AMUpdatesViewProps) => {
  const toast = useToast();
  const [updates, setUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    total: 0,
  });

  const fetchUpdatesData = useCallback(async () => {
    if (!partnerId) return;

    setLoading(true);
    try {
      const result = await fetchAMUpdates(partnerId, pagination.page, pagination.limit);

      setUpdates(result.items);
      setPagination((prev) => ({
        ...prev,
        total: result.count,
      }));
    } catch (error) {
      console.error("Error fetching updates:", error);
      toast({
        title: "Error",
        description: "Failed to fetch AM updates",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [partnerId, pagination.page, pagination.limit, toast]);

  useEffect(() => {
    fetchUpdatesData();
  }, [fetchUpdatesData]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handlePageStep = (step: number) => {
    const nextPage = Math.min(
      totalPages,
      Math.max(1, pagination.page + step)
    );

    if (nextPage !== pagination.page) {
      handlePageChange(nextPage);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination((prev) => ({
      ...prev,
      page: 1,
      limit: newLimit,
    }));
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  return (
    <Box bg="white" borderRadius={"16"} boxShadow="sm" p={6} mt={8}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">CRM</Heading>
      </Flex>

      {loading ? (
        <Box>
          <Skeleton height="40px" mb={4} />
          <Skeleton height="200px" />
        </Box>
      ) : updates.length > 0 ? (
        <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <Table variant="simple" colorScheme="gray">
            <Thead>
              <Tr>
                <Th>Date</Th>
                <Th>Categories</Th>
                <Th>Update Note</Th>
              </Tr>
            </Thead>
            <Tbody>
              {updates.map((update, index) => (
                <Tr key={update.id} bg={index % 2 === 0 ? "gray.50" : "white"}>
                  <Td>{format(new Date(update.date), "MMM dd, yyyy")}</Td>
                  <Td>
                    <HStack spacing={2}>
                      {update.updateCategories.map((category) => {
                        const categoryData = UPDATE_CATEGORIES.find(
                          (c) => c.title === category
                        );
                        return (
                          <Tag key={category} colorScheme={categoryData?.color}>
                            <TagLabel>{category}</TagLabel>
                          </Tag>
                        );
                      })}
                    </HStack>
                  </Td>
                  <Td>
                    <Text>{update.updateNote}</Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          <Flex justify="space-between" align="center" mt={4}>
            <HStack spacing={2}>
              <Text>Rows per page:</Text>
              <Select
                value={pagination.limit}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleLimitChange(Number(e.target.value))
                }
                width="80px"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Select>
            </HStack>
            <HStack spacing={2}>
              <Button
                size="sm"
                onClick={() => handlePageStep(-2)}
                isDisabled={pagination.page === 1}
              >
                {"<<"}
              </Button>
              <Button
                size="sm"
                onClick={() => handlePageStep(-1)}
                isDisabled={pagination.page === 1}
              >
                {"<"}
              </Button>
              <Text>
                Page {pagination.page} of {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => handlePageStep(1)}
                isDisabled={pagination.page >= totalPages}
              >
                {">"}
              </Button>
              <Button
                size="sm"
                onClick={() => handlePageStep(2)}
                isDisabled={pagination.page >= totalPages}
              >
                {">>"}
              </Button>
            </HStack>
          </Flex>
        </Box>
      ) : (
        <Text color="gray.500">No updates available for this partner.</Text>
      )}
    </Box>
  );
};

export default AMUpdatesView;
